import { EtiquetaApontada } from './apontamentos';

/**
 * Materiais do pedido no card do Mapa Cirúrgico.
 *
 * O `item_pedido` que o GET /pedidos/:id devolve é gravado UMA vez, quando o
 * pedido nasce: o webhook do ERP não regrava os itens nas alterações seguintes
 * (ESF-API, processarWebhookPedidoUseCase). Só que o pedido de cirurgia é
 * digitado com o kit (vários tamanhos) e depois reduzido no TOTVS ao que foi
 * usado — e o card seguia mostrando o kit inteiro (ex.: 083404, 6 linhas no
 * mapa e 1 no TOTVS).
 *
 * Duas fontes dizem como o pedido está no TOTVS, nesta ordem:
 *
 * 1. `payload_recebido.itens`, o corpo do último webhook. Só existe nos avisos
 *    de inclusão/alteração: o de NOTA_GERADA vem sem itens e sobrescreve o
 *    anterior. Por isso a última lista vista fica guardada no navegador (Fase
 *    1, mesmo padrão dos carimbos do mapa) e vale depois da nota.
 * 2. O apontamento do app, quando os materiais apontados fecham com o valor
 *    atual do pedido — é o caso do pedido reduzido ao que foi usado.
 *
 * Sem nenhuma das duas, fica o `item_pedido`. Tudo isto sai quando o backend
 * passar a regravar os itens no webhook.
 */

export interface MaterialPedido {
  id: string;
  descricao: string;
  codigo?: string;
  quantidade: number;
  /** Preço unitário gravado no item — só para conferir os apontados contra o valor do pedido. */
  precoUnitario?: number;
}

export interface ItemErp {
  codigo: string;
  quantidade: number;
}

interface ItensLembrados {
  /** Valor do pedido no aviso que trouxe a lista. */
  valor: number;
  itens: ItemErp[];
  vistoEm: string;
}

/** pedido_id → última lista de itens que o TOTVS mandou. */
export type MemoriaItensErp = Record<string, ItensLembrados>;

export const CHAVE_MEMORIA_ITENS_ERP = 'surgicalMapItensErpV1';

const DIAS_NA_MEMORIA = 45;
const DIA_MS = 24 * 60 * 60 * 1000;

const mesmoValor = (a: number, b: number) => Math.abs(a - b) < 0.01;

// Maior quantidade primeiro: é o que a logística confere antes.
const porQuantidade = (a: MaterialPedido, b: MaterialPedido) =>
  b.quantidade - a.quantidade || a.descricao.localeCompare(b.descricao);

/** Itens gravados no pedido (`item_pedido` do GET /pedidos/:id). */
export function normalizaMateriais(pedido: any): MaterialPedido[] {
  const itens: any[] = Array.isArray(pedido?.item_pedido) ? pedido.item_pedido : [];
  return itens
    .filter((i) => !i?.excluido)
    .map((i, idx) => ({
      id: (i?.item_pedido_id ?? `${i?.produto_id ?? 'item'}-${idx}`).toString(),
      descricao: (
        i?.produtos?.nome || i?.produtos?.apelido || i?.produtos?.referencia || 'Produto sem descrição'
      ).toString().trim(),
      codigo: (i?.produtos?.codigo ?? '').toString().trim() || undefined,
      quantidade: Number(i?.quantidade ?? 0) || 0,
      precoUnitario: i?.preco_liquido == null ? undefined : Number(i.preco_liquido),
    }))
    .sort(porQuantidade);
}

/** Itens do último webhook; null quando ele não trouxe a lista (o de NF vem sem). */
export function itensDoWebhook(payloadRecebido: any): ItemErp[] | null {
  let payload = payloadRecebido;
  if (typeof payload === 'string') {
    try { payload = JSON.parse(payload); } catch { return null; }
  }
  if (!Array.isArray(payload?.itens)) return null;

  const itens: ItemErp[] = payload.itens
    .map((i: any) => ({
      codigo: String(i?.codigo_produto ?? '').trim(),
      quantidade: Number(i?.quantidade) || 0,
    }))
    .filter((i: ItemErp) => !!i.codigo);
  return itens.length > 0 ? itens : null;
}

const mesmosItens = (a: ItemErp[], b: ItemErp[]) =>
  a.length === b.length
  && a.every((item, i) => item.codigo === b[i].codigo && item.quantidade === b[i].quantidade);

/**
 * Lista do TOTVS de cada pedido (por pedido_id): a do webhook atual ou, quando
 * ele já foi sobrescrito pelo da nota, a última que este navegador viu.
 *
 * A lembrada só vale enquanto o valor do pedido for o do aviso que a trouxe: o
 * valor só muda em aviso com a lista completa, então valor diferente é sinal de
 * alteração que este navegador não viu — melhor o `item_pedido` que uma lista
 * que já se sabe velha.
 *
 * `mudou` avisa quando a memória precisa ser regravada.
 */
export function itensErpPorPedido(
  pedidos: any[],
  memoriaAtual: MemoriaItensErp,
  agora: string
): { porPedido: Record<string, ItemErp[]>; memoria: MemoriaItensErp; mudou: boolean } {
  const memoria: MemoriaItensErp = { ...memoriaAtual };
  const porPedido: Record<string, ItemErp[]> = {};
  let mudou = false;

  (Array.isArray(pedidos) ? pedidos : []).forEach((pedido) => {
    const id = pedido?.pedido_id;
    if (!id) return;

    const valor = Number(pedido.valor_pedido) || 0;
    const doWebhook = itensDoWebhook(pedido.payload_recebido);
    const lembrado = memoria[id];

    if (doWebhook) {
      porPedido[id] = doWebhook;
      if (!lembrado || !mesmoValor(lembrado.valor, valor) || !mesmosItens(lembrado.itens, doWebhook)) {
        memoria[id] = { valor, itens: doWebhook, vistoEm: agora };
        mudou = true;
      }
      return;
    }

    if (!lembrado) return;
    if (mesmoValor(lembrado.valor, valor)) {
      porPedido[id] = lembrado.itens;
      return;
    }
    delete memoria[id];
    mudou = true;
  });

  const limite = new Date(agora).getTime() - DIAS_NA_MEMORIA * DIA_MS;
  Object.keys(memoria).forEach((id) => {
    if (new Date(memoria[id].vistoEm).getTime() < limite) {
      delete memoria[id];
      mudou = true;
    }
  });

  return { porPedido, memoria, mudou };
}

/**
 * A lista do TOTVS com o nome tirado dos itens gravados (mesmo código). Produto
 * incluído no TOTVS depois da criação não tem nome aqui — aparece pelo código.
 */
export function materiaisConformeErp(itensErp: ItemErp[], gravados: MaterialPedido[]): MaterialPedido[] {
  const nomePorCodigo = new Map<string, string>();
  gravados.forEach((m) => {
    if (m.codigo && !nomePorCodigo.has(m.codigo)) nomePorCodigo.set(m.codigo, m.descricao);
  });

  return itensErp
    .map((item, idx) => ({
      id: `erp-${idx}-${item.codigo}`,
      descricao: nomePorCodigo.get(item.codigo) || `Produto ${item.codigo}`,
      codigo: item.codigo,
      quantidade: item.quantidade,
    }))
    .sort(porQuantidade);
}

// Uma etiqueta é uma unidade lida; `quantidade` só vem quando o app informa outra.
function apontadosQueFecham(
  etiquetas: EtiquetaApontada[],
  gravados: MaterialPedido[],
  valorPedido: number
): MaterialPedido[] | null {
  const gravadoPorCodigo = new Map<string, MaterialPedido>();
  gravados.forEach((m) => {
    if (m.codigo && !gravadoPorCodigo.has(m.codigo)) gravadoPorCodigo.set(m.codigo, m);
  });

  const quantidadePorCodigo = new Map<string, number>();
  const semCodigo = etiquetas.some((etiqueta) => {
    const codigo = String(etiqueta?.produto_codigo || '').trim();
    if (!codigo) return true;
    quantidadePorCodigo.set(codigo, (quantidadePorCodigo.get(codigo) || 0) + (Number(etiqueta.quantidade) || 1));
    return false;
  });
  if (semCodigo || quantidadePorCodigo.size === 0) return null;

  let total = 0;
  let semPreco = false;
  const lista: MaterialPedido[] = [];
  quantidadePorCodigo.forEach((quantidade, codigo) => {
    const gravado = gravadoPorCodigo.get(codigo);
    if (!gravado || gravado.precoUnitario == null) {
      semPreco = true;
      return;
    }
    total += quantidade * gravado.precoUnitario;
    lista.push({ id: `apontado-${codigo}`, descricao: gravado.descricao, codigo, quantidade });
  });

  return !semPreco && mesmoValor(total, valorPedido) ? lista.sort(porQuantidade) : null;
}

/**
 * Materiais apontados no app, SE fecharem com o valor atual do pedido — a prova
 * de que o pedido foi reduzido no TOTVS ao que foi usado. Sem fechar (pedido
 * que segue com o kit, preço renegociado, material fora do pedido), devolve
 * null e o card fica com a lista que tinha.
 *
 * Cada posição de `apontamentos` são as etiquetas de um apontamento do pedido.
 * A mesma cirurgia pode ter dois (um por filial de baixa), então tenta todos
 * juntos e depois cada um sozinho.
 */
export function materiaisApontados(
  apontamentos: EtiquetaApontada[][],
  gravados: MaterialPedido[],
  valorPedido?: number
): MaterialPedido[] | null {
  if (!valorPedido || apontamentos.length === 0) return null;

  const juntos = ([] as EtiquetaApontada[]).concat(...apontamentos);
  const tentativas = apontamentos.length > 1 ? [juntos, ...apontamentos] : apontamentos;
  for (let i = 0; i < tentativas.length; i += 1) {
    const lista = apontadosQueFecham(tentativas[i], gravados, valorPedido);
    if (lista) return lista;
  }
  return null;
}

interface FontesDeMateriais {
  /** `item_pedido` já carregado; undefined = ainda buscando. */
  gravados?: MaterialPedido[];
  itensErp?: ItemErp[];
  /** Etiquetas de cada apontamento do pedido. */
  apontamentos?: EtiquetaApontada[][];
  valorPedido?: number;
}

/** O que o card mostra em MATERIAIS; undefined enquanto os itens não chegam. */
export function materiaisDoPedido(fontes: FontesDeMateriais): MaterialPedido[] | undefined {
  const { gravados, itensErp, apontamentos = [], valorPedido } = fontes;
  if (!gravados) return undefined;
  if (itensErp) return materiaisConformeErp(itensErp, gravados);
  return materiaisApontados(apontamentos, gravados, valorPedido) || gravados;
}
